import { t } from "./_i18n.js";
import { chain } from "./_redirect_chain.js";
import { toolError, toolUsed } from "./_tool_event.js";
import { assess, targetURLs, validReport } from "./_security_checks.js";

const TOOL = "security-checker";
const form = document.getElementById("security-form");
const input = document.getElementById("security-url");
const button = document.getElementById("security-submit");
const status = document.getElementById("security-status");
const out = document.getElementById("security-result");
let running = false;
const labels = {
    fail: t("sec-js-badge-fail"), warning: t("sec-js-badge-warning"),
    unknown: t("sec-js-badge-unknown"), pass: t("sec-js-badge-pass"),
};

if (form && input && button && status && out) {
    // Prefill the handoff from the SSL checker, but never run a scan on a GET.
    const prefill = new URLSearchParams(window.location.search).get("url");
    if (prefill) {
        try { input.value = targetURLs(prefill).https; } catch { /* Ignore invalid handoffs. */ }
    }
    form.addEventListener("submit", e => { e.preventDefault(); run(); });
}

function el(tag, cls, text) {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text !== undefined) node.textContent = text;
    return node;
}

async function probe(url, kind) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 16000);
    try {
        const response = await fetch(url, { headers: { accept: "application/json" }, signal: controller.signal });
        let body;
        try { body = await response.json(); }
        catch { return { error: t("sec-js-probe-unreadable") }; }
        if (!response.ok || body?.ok !== true) {
            toolError(TOOL, { reason: response.ok ? "probe-incomplete" : `status-${response.status}` });
            // The probe writes its own sentences in English.
            return typeof body?.error === "string" ? { error: body.error, lang: "en" } : { error: t("sec-js-probe-incomplete") };
        }
        if (!validReport(kind, body)) return { error: t("sec-js-probe-partial") };
        return { report: body };
    } catch (error) {
        toolError(TOOL, { reason: error.name === "AbortError" ? "timeout" : "probe-unreachable" });
        return { error: t(error.name === "AbortError" ? "sec-js-probe-timeout" : "sec-js-probe-unreachable") };
    } finally { clearTimeout(timeout); }
}

async function run() {
    if (running) return;
    let target;
    try { target = targetURLs(input.value); }
    catch (error) {
        status.textContent = error.message;
        out.replaceChildren();
        input.setAttribute("aria-invalid", "true");
        input.focus();
        toolError(TOOL, { reason: "invalid-url" });
        return;
    }
    running = true;
    button.disabled = true;
    input.setAttribute("aria-invalid", "false");
    form.setAttribute("aria-busy", "true");
    out.replaceChildren();
    status.textContent = t("sec-js-status-reading");
    toolUsed(TOOL); // Never send the submitted domain, path or query to analytics.
    try {
        const [ssl, https] = await Promise.all([
            probe(`${form.dataset.sslProbe}?host=${encodeURIComponent(target.host)}&port=443`, "ssl"),
            probe(`${form.dataset.headerProbe}?url=${encodeURIComponent(target.https)}`, "headers"),
        ]);
        // Reuse the same header limiter, sequentially. Long chains can consume
        // its remaining budget; that produces an explicit incomplete check.
        status.textContent = t("sec-js-status-http");
        const http = await probe(`${form.dataset.headerProbe}?url=${encodeURIComponent(target.http)}`, "headers");
        const checks = assess(target, ssl, https, http);
        render(target, checks, https.report, http.report);
        status.textContent = t("sec-js-status-ready");
    } catch {
        status.textContent = t("sec-js-status-failed");
        toolError(TOOL, { reason: "report-error" });
    } finally {
        running = false;
        button.disabled = false;
        form.setAttribute("aria-busy", "false");
    }
}

// One line a person can act on before reading anything else: what failed,
// or what is left to look at.
function headline(counts, total) {
    if (counts.fail) return [t("sec-js-headline-failed", { count: counts.fail }), "down"];
    if (counts.warning) return [t("sec-js-headline-review", { count: counts.warning }), "warn"];
    if (counts.unknown) return [t("sec-js-headline-unknown", { count: counts.unknown }), "quiet"];
    return [t("sec-js-headline-passed", { count: total }), "ok"];
}

function verdict(target, checks, https) {
    const counts = Object.fromEntries(Object.keys(labels).map(key => [key, checks.filter(c => c.status === key).length]));
    const [text, tone] = headline(counts, checks.length);
    const block = el("div", `tool-security__verdict mk-mono tool-security__verdict--${tone}`);
    block.append(el("p", "tool-security__headline", text));
    block.append(el("p", "tool-security__counts", [
        t("sec-js-count-checks", { count: checks.length }),
        t("sec-js-count-failed", { count: counts.fail }),
        t("sec-js-count-warnings", { count: counts.warning }),
        t("sec-js-count-unknown", { count: counts.unknown }),
        t("sec-js-count-passed", { count: counts.pass }),
    ].join(" · ")));
    if (https && https.final_url !== target.https) {
        block.append(el("p", "tool-security__counts", t("sec-js-headers-from", { url: https.final_url })));
    }
    return block;
}

function findings(checks) {
    const list = el("ol", "tool-security__findings");
    for (const check of checks) {
        const item = el("li", `tool-security__finding tool-security__finding--${check.status}`);
        item.append(el("span", "tool-security__badge mk-mono", labels[check.status]));
        item.append(el("h3", "tool-security__title", check.title));
        const evidence = el("p", "tool-security__evidence mk-mono", check.evidence);
        if (check.lang) evidence.lang = check.lang;
        item.append(evidence);
        item.append(el("p", "tool-security__advice mk-body", check.advice));
        list.append(item);
    }
    return list;
}

function chains(https, http) {
    const frag = document.createDocumentFragment();
    for (const [label, report] of [[t("sec-js-chain-https"), https], [t("sec-js-chain-http"), http]]) {
        if (!report) continue;
        const details = el("details", "tool-security__chain");
        const requests = t("sec-js-chain-requests", { count: report.hops.length });
        details.append(el("summary", "mk-mono", `${label} · ${requests} · ${t("redirect-chain-ms", { ms: report.total_ms })}`));
        details.append(chain(report));
        frag.append(details);
    }
    return frag;
}

// Plain text for a ticket or a message to whoever runs the server.
function plainText(target, checks) {
    const lines = [t("sec-js-report-title", { host: target.host }), `${new Date().toISOString()} · ${location.origin}${location.pathname}`, ""];
    for (const check of checks) {
        lines.push(`[${labels[check.status]}] ${check.title}`, `  ${check.evidence}`, `  ${check.advice}`, "");
    }
    return lines.join("\n");
}

function copyButton(target, checks) {
    const copy = el("button", "mk-cta mk-cta--ghost", t("sec-js-copy"));
    copy.type = "button";
    copy.addEventListener("click", async () => {
        window.umami?.track("tool-copy", { tool: TOOL });
        try {
            await navigator.clipboard.writeText(plainText(target, checks));
            copy.textContent = t("sec-js-copied");
        } catch {
            copy.textContent = t("sec-js-copy-manually");
        }
        setTimeout(() => { copy.textContent = t("sec-js-copy"); }, 1600);
    });
    return copy;
}

function render(target, checks, https, http) {
    const fragment = document.createDocumentFragment();
    const head = el("p", "tool-dns__head mk-mono");
    head.append(el("span", "tool-dns__q", target.host));
    fragment.append(head, verdict(target, checks, https), findings(checks), chains(https, http));
    fragment.append(el("p", "tool-dns__note", t("sec-js-scope-note")));

    const actions = el("div", "tool-security__actions");
    // Only offer a handoff when at least one probe returned usable evidence.
    if (checks.some(c => c.status !== "unknown")) {
        for (const [text, kind, value, cls] of [
            [t("sec-js-monitor-website"), "http", target.https, "mk-cta--primary"],
            [t("sec-js-monitor-certificate"), "tls_cert", target.host, "mk-cta--ghost"],
        ]) {
            const link = el("a", `mk-cta ${cls}`, text);
            link.href = `/start?kind=${kind}&url=${encodeURIComponent(value)}`;
            link.dataset.umamiEvent = "signup-start";
            link.dataset.umamiEventPosition = "tool-security-result";
            actions.append(link);
        }
        actions.append(copyButton(target, checks));
    }
    fragment.append(actions);
    out.replaceChildren(fragment);
}
